import { useEffect, useState } from "react";
import StatCard from "pages/dashboard-overview/components/StatCard";
import StatCardSkeleton from "pages/analytics/components/StatCardSkeleton";
import {
  formatCurrency,
  formatPercent,
} from "pages/analytics/components/analyticsFormatters";
import { getKpis, getProfitLoss } from "services/finance.service";
import { subscribeCommissionUpdated } from "utils/commissionEvents";

const isCustomRangeIncomplete = (filters) =>
  filters?.dateRangeType === "custom" &&
  (!filters?.customDateRange?.[0] || !filters?.customDateRange?.[1]);

export default function ProfitLossSummary({ api, filters, refreshKey = 0 }) {
  const [data, setData] = useState(null);
  const [profitMargin, setProfitMargin] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isCustomRangeIncomplete(filters)) {
      setIsLoading(false);
      setData(null);
      setProfitMargin(null);
      setError(null);
      return;
    }

    let cancelled = false;

    const load = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const [result, kpis] = await Promise.all([
          getProfitLoss(api, { filters }),
          getKpis(api, { filters }),
        ]);
        if (!cancelled) {
          setData(result ?? null);
          setProfitMargin(kpis?.profitMargin ?? null);
        }
      } catch {
        if (!cancelled) {
          setData(null);
          setProfitMargin(null);
          setError("Failed to load profit & loss data. Please try again.");
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [
    filters.dateRangeType,
    filters.customDateRange?.[0],
    filters.customDateRange?.[1],
    refreshKey,
  ]);

  useEffect(() => {
    if (isCustomRangeIncomplete(filters)) return undefined;
    return subscribeCommissionUpdated(() => {
      Promise.all([
        getProfitLoss(api, { filters }),
        getKpis(api, { filters }),
      ])
        .then(([result, kpis]) => {
          setData(result ?? null);
          setProfitMargin(kpis?.profitMargin ?? null);
        })
        .catch(() => {});
    });
  }, [
    api,
    filters.dateRangeType,
    filters.customDateRange?.[0],
    filters.customDateRange?.[1],
  ]);

  const profit = data?.profit ?? 0;
  const breakdown = data?.breakdown ?? {};

  const plCards = [
    { title: "Revenue", value: formatCurrency(data?.revenue), icon: "DollarSign" },
    { title: "Expenses", value: formatCurrency(data?.expenses), icon: "CreditCard" },
    {
      title: "Delivery Cost",
      value: formatCurrency(data?.deliveryCost),
      icon: "Package",
    },
    {
      title: "Gross Profit",
      value: formatCurrency(data?.grossProfit),
      icon: "TrendingUp",
    },
    {
      title: profit >= 0 ? "Net Profit" : "Net Loss",
      value: formatCurrency(profit),
      icon: profit >= 0 ? "PieChart" : "TrendingDown",
    },
    {
      title: "Profit Margin",
      value: formatPercent(profitMargin),
      icon: "Percent",
    },
    {
      title: "Gross Margin %",
      value: formatPercent(data?.grossMarginPct),
      icon: "PieChart",
    },
    {
      title: "Commissions",
      value: formatCurrency(data?.commissions),
      icon: "Percent",
    },
  ];

  const expenseBreakdownCards = [
    {
      title: "Team Expenses",
      value: formatCurrency(breakdown.team_current),
      icon: "Users",
    },
    {
      title: "Subscriptions",
      value: formatCurrency(breakdown.subscriptions),
      icon: "Repeat",
    },
    {
      title: "Other Expenses",
      value: formatCurrency(breakdown.other_costs),
      icon: "Pencil",
    },
  ];

  const commissionBreakdownCards = [
    {
      title: "Email Commissions",
      value: formatCurrency(breakdown.email_commissions),
      icon: "Mail",
    },
    {
      title: "Auto Commissions",
      value: formatCurrency(breakdown.auto_commissions),
      icon: "Percent",
    },
    {
      title: "Sales Commissions",
      value: formatCurrency(breakdown.sales_commissions),
      icon: "Trophy",
    },
  ];

  const showEmpty =
    !isLoading && !error && !isCustomRangeIncomplete(filters) && !data;

  return (
    <>
      <div className="mb-6">
        <h2 className="text-lg font-medium text-gray-900 mb-3">Profit & Loss</h2>

        {error && (
          <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {error}
          </div>
        )}

        {isCustomRangeIncomplete(filters) && !isLoading && (
          <p className="text-sm text-gray-500 italic">
            Select start and end dates to view profit & loss.
          </p>
        )}

        {showEmpty && (
          <p className="text-sm text-gray-500 italic">
            No data available for the selected period.
          </p>
        )}

        {(isLoading || data) && !isCustomRangeIncomplete(filters) && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {isLoading
              ? [...Array(8)].map((_, i) => <StatCardSkeleton key={i} />)
              : plCards.map((card) => (
                  <StatCard
                    key={card.title}
                    title={card.title}
                    value={card.value}
                    icon={card.icon}
                  />
                ))}
          </div>
        )}
      </div>

      {/* <div className="mb-6">
        <h2 className="text-lg font-medium text-gray-900 mb-3">Expense Breakdown</h2>

        {error && !isLoading && (
          <p className="text-sm text-gray-500 italic">
            Breakdown unavailable due to a loading error.
          </p>
        )}

        {showEmpty && (
          <p className="text-sm text-gray-500 italic">
            No breakdown data for the selected period.
          </p>
        )}

        {isCustomRangeIncomplete(filters) && !isLoading && (
          <p className="text-sm text-gray-500 italic">
            Select start and end dates to view breakdown.
          </p>
        )}

        {(isLoading || data) && !isCustomRangeIncomplete(filters) && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {isLoading
              ? [...Array(3)].map((_, i) => <StatCardSkeleton key={i} />)
              : expenseBreakdownCards.map((card) => (
                  <StatCard
                    key={card.title}
                    title={card.title}
                    value={card.value}
                    icon={card.icon}
                  />
                ))}
          </div>
        )}
      </div> */}

      {/* <div className="mb-6">
        <h2 className="text-lg font-medium text-gray-900 mb-3">Commission Breakdown</h2>

        {error && !isLoading && (
          <p className="text-sm text-gray-500 italic">
            Breakdown unavailable due to a loading error.
          </p>
        )}

        {showEmpty && (
          <p className="text-sm text-gray-500 italic">
            No breakdown data for the selected period.
          </p>
        )}

        {isCustomRangeIncomplete(filters) && !isLoading && (
          <p className="text-sm text-gray-500 italic">
            Select start and end dates to view breakdown.
          </p>
        )}

        {(isLoading || data) && !isCustomRangeIncomplete(filters) && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {isLoading
              ? [...Array(3)].map((_, i) => <StatCardSkeleton key={i} />)
              : commissionBreakdownCards.map((card) => (
                  <StatCard
                    key={card.title}
                    title={card.title}
                    value={card.value}
                    icon={card.icon}
                  />
                ))}
          </div>
        )}
      </div> */}
    </>
  );
}
